import { useState } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiHelpers, aiHelpers } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import type { AIGeneratedBlogPost } from '@/lib/api';
import type { BlogPost, BlogPostStatus, CreateBlogPostData } from '@/types';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import {
  ArrowLeft,
  Loader2,
  X,
  Search,
  ImagePlus,
  Palette,
  Copy,
  Check,
  ChevronDown,
  ChevronUp,
  Code,
  Eye,
} from 'lucide-react';

/**
 * Full-page blog post editor (create + edit) — replaces the old modal.
 * Content is edited in a WYSIWYG rich-text editor by default (business users
 * see the post as it reads, no HTML tags), with an "Edit HTML" toggle for
 * power users. Reached at /projects/:projectId/blog/new and
 * /projects/:projectId/blog/:postId/edit.
 */
export default function BlogPostEditor() {
  const { projectId, postId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEditing = Boolean(postId);

  // AI-generated prefill is passed via router state from the list page.
  const aiGeneratedData = (
    location.state as { aiGeneratedData?: { post: AIGeneratedBlogPost; image?: string } } | null
  )?.aiGeneratedData;
  const initialData = aiGeneratedData?.post;

  const backToList = () => navigate(`/projects/${projectId}/blog`);

  // Load the post being edited.
  const { data: postData, isLoading: loadingPost } = useQuery({
    queryKey: ['blog-post', projectId, postId],
    queryFn: () => apiHelpers.getBlogPost(projectId!, postId!),
    enabled: Boolean(projectId && postId),
  });
  const post: BlogPost | undefined = postData?.data?.data;

  const { data: categoriesData } = useQuery({
    queryKey: qk.blogCategories(projectId),
    queryFn: () => apiHelpers.getBlogCategories(projectId!),
    enabled: Boolean(projectId),
  });
  const categories = categoriesData?.data?.data || [];

  if (isEditing && loadingPost) {
    return (
      <div className="text-muted-foreground flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <EditorForm
      key={post?.id || 'new'}
      projectId={projectId!}
      post={post || null}
      categories={categories}
      initialData={initialData}
      initialImage={aiGeneratedData?.image}
      isEditing={isEditing}
      onDone={backToList}
    />
  );
}

function EditorForm({
  projectId,
  post,
  categories,
  initialData,
  initialImage,
  isEditing,
  onDone,
}: {
  projectId: string;
  post: BlogPost | null;
  categories: Array<{ id: string; name: string }>;
  initialData?: AIGeneratedBlogPost;
  initialImage?: string;
  isEditing: boolean;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();

  const [title, setTitle] = useState(post?.title || initialData?.title || '');
  const [slug, setSlug] = useState(post?.slug || initialData?.slug || '');
  const [excerpt, setExcerpt] = useState(post?.excerpt || initialData?.excerpt || '');
  const [content, setContent] = useState(post?.content || initialData?.content || '');
  const [htmlMode, setHtmlMode] = useState(false);
  const [status, setStatus] = useState<BlogPostStatus>(post?.status || 'draft');
  const [categoryId, setCategoryId] = useState(post?.categoryId || '');
  const [tagsInput, setTagsInput] = useState(
    post?.tags?.join(', ') || initialData?.tags?.join(', ') || ''
  );
  const [featuredImage, setFeaturedImage] = useState(post?.featuredImage || initialImage || '');
  const [metaTitle, setMetaTitle] = useState(
    post?.seo?.metaTitle || initialData?.seo?.metaTitle || ''
  );
  const [metaDescription, setMetaDescription] = useState(
    post?.seo?.metaDescription || initialData?.seo?.metaDescription || ''
  );
  const [error, setError] = useState('');

  // AI image tooling state
  const [showImageGenerator, setShowImageGenerator] = useState(false);
  const [imagePrompt, setImagePrompt] = useState('');
  const [imageStyle, setImageStyle] = useState<'photo' | 'illustration' | 'digital-art' | '3d'>(
    'photo'
  );
  const [generatingImagePrompts, setGeneratingImagePrompts] = useState(false);
  const [suggestedImagePrompts, setSuggestedImagePrompts] = useState<string[]>([]);
  const [generatingImage, setGeneratingImage] = useState<string | null>(null);
  const [searchingImages, setSearchingImages] = useState(false);
  const [searchedImages, setSearchedImages] = useState<
    Array<{ id: string; url: string; thumbnailUrl: string; alt: string; photographer: string }>
  >([]);

  const onSaved = (msg: string) => {
    queryClient.invalidateQueries({ queryKey: qk.blogPosts(projectId) });
    toast.success(msg);
    onDone();
  };
  const onErr = (
    err: Error & { response?: { data?: { error?: { message?: string } }; status?: number } }
  ) => {
    if (err.response?.status === 401) {
      setError('Your session has expired. Please copy your content elsewhere and log in again.');
    } else {
      setError(err.response?.data?.error?.message || 'Failed to save post');
    }
  };

  const createMutation = useMutation({
    mutationFn: (data: CreateBlogPostData) => apiHelpers.createBlogPost(projectId, data),
    onSuccess: () => onSaved('Post created'),
    onError: onErr,
  });
  const updateMutation = useMutation({
    mutationFn: (data: CreateBlogPostData) => apiHelpers.updateBlogPost(projectId, post!.id, data),
    onSuccess: () => onSaved('Post updated'),
    onError: onErr,
  });
  const isPending = createMutation.isPending || updateMutation.isPending;

  const generateSlug = (text: string) =>
    text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return setError('Title is required');
    if (!content.trim() || content.trim() === '<p></p>') return setError('Content is required');

    const data: CreateBlogPostData = {
      title: title.trim(),
      slug: slug.trim() || undefined,
      excerpt: excerpt.trim() || undefined,
      content,
      contentFormat: 'html',
      status,
      categoryId: categoryId || undefined,
      tags: tagsInput
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean),
      featuredImage: featuredImage.trim() || undefined,
      seo:
        metaTitle || metaDescription
          ? {
              metaTitle: metaTitle.trim() || undefined,
              metaDescription: metaDescription.trim() || undefined,
            }
          : undefined,
    };
    if (isEditing) updateMutation.mutate(data);
    else createMutation.mutate(data);
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-6">
      <Link
        to={`/projects/${projectId}/blog`}
        className="text-muted-foreground hover:text-foreground mb-4 inline-flex items-center gap-1 text-sm"
      >
        <ArrowLeft className="h-4 w-4" /> Back to Blog
      </Link>

      <form onSubmit={handleSubmit}>
        <div className="mb-6 flex items-center justify-between gap-4">
          <h1 className="text-2xl font-semibold">{isEditing ? 'Edit Post' : 'New Post'}</h1>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onDone}
              className="hover:bg-muted rounded-lg border px-4 py-2 text-sm font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="bg-primary text-primary-foreground hover:bg-primary/90 inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium disabled:opacity-50"
            >
              {isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {isEditing ? 'Update Post' : 'Create Post'}
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600 dark:border-red-800 dark:bg-red-900/20">
            {error}
          </div>
        )}

        <div className="space-y-4">
          {/* Title */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={() => {
                if (!isEditing && !slug && title) setSlug(generateSlug(title));
              }}
              placeholder="Enter post title"
              className="bg-background focus:ring-primary w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2"
            />
          </div>

          {/* Slug */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Slug</label>
            <input
              type="text"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="url-friendly-slug (auto-generated if empty)"
              className="bg-background focus:ring-primary w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2"
            />
          </div>

          {/* Status & Category */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as BlogPostStatus)}
                className="bg-background focus:ring-primary w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2"
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="scheduled">Scheduled</option>
                <option value="archived">Archived</option>
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Category</label>
              <select
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                className="bg-background focus:ring-primary w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2"
              >
                <option value="">Uncategorized</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Excerpt */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Excerpt</label>
            <textarea
              value={excerpt}
              onChange={(e) => setExcerpt(e.target.value)}
              placeholder="Brief summary of the post (optional)"
              rows={2}
              className="bg-background focus:ring-primary w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2"
            />
          </div>

          {/* Content — WYSIWYG by default, HTML source on toggle */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Content *</label>
              <button
                type="button"
                onClick={() => setHtmlMode((v) => !v)}
                className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-xs"
                title={htmlMode ? 'Switch to visual editor' : 'Edit raw HTML'}
              >
                {htmlMode ? <Eye className="h-3.5 w-3.5" /> : <Code className="h-3.5 w-3.5" />}
                {htmlMode ? 'Visual editor' : 'Edit HTML'}
              </button>
            </div>
            {htmlMode ? (
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Write HTML here..."
                rows={18}
                className="bg-background focus:ring-primary w-full rounded-lg border px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2"
              />
            ) : (
              <RichTextEditor content={content} onChange={setContent} />
            )}
          </div>

          {/* Tags */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Tags</label>
            <input
              type="text"
              value={tagsInput}
              onChange={(e) => setTagsInput(e.target.value)}
              placeholder="tag1, tag2, tag3 (comma separated)"
              className="bg-background focus:ring-primary w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2"
            />
          </div>

          {/* Featured Image + AI tooling */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Featured Image</label>
            <div className="space-y-3">
              <input
                type="url"
                value={featuredImage}
                onChange={(e) => setFeaturedImage(e.target.value)}
                placeholder="https://example.com/image.jpg"
                className="bg-background focus:ring-primary w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2"
              />
              {featuredImage && (
                <div className="relative overflow-hidden rounded-lg border">
                  <img
                    src={featuredImage}
                    alt="Featured"
                    className="h-40 w-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setFeaturedImage('')}
                    className="absolute right-2 top-2 rounded-full bg-black/50 p-1 text-white hover:bg-black/70"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}

              <button
                type="button"
                onClick={() => setShowImageGenerator(!showImageGenerator)}
                className="text-muted-foreground hover:text-foreground flex items-center gap-2 text-sm font-medium transition-colors"
              >
                {showImageGenerator ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
                <ImagePlus className="h-4 w-4" />
                AI Image Tools
              </button>

              {showImageGenerator && (
                <div className="bg-muted/50 space-y-3 rounded-lg border p-3">
                  {/* Search images */}
                  <div className="space-y-2">
                    <label className="text-muted-foreground text-xs font-medium">
                      Search for images
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={imagePrompt}
                        onChange={(e) => setImagePrompt(e.target.value)}
                        placeholder={title || 'Search term...'}
                        className="bg-background focus:ring-primary flex-1 rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2"
                      />
                      <button
                        type="button"
                        onClick={async () => {
                          const query = imagePrompt.trim() || title.trim();
                          if (!query) return;
                          setSearchingImages(true);
                          try {
                            const response = await aiHelpers.searchImages(query, 8);
                            if (response.data.success && response.data.data) {
                              setSearchedImages(response.data.data);
                            }
                          } catch {
                            toast.error('Failed to search images');
                          }
                          setSearchingImages(false);
                        }}
                        disabled={searchingImages || (!imagePrompt.trim() && !title.trim())}
                        className="inline-flex items-center gap-1 rounded-lg bg-gradient-to-r from-blue-500 to-cyan-500 px-3 py-2 text-sm font-medium text-white transition-all hover:from-blue-600 hover:to-cyan-600 disabled:opacity-50"
                      >
                        {searchingImages ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Search className="h-4 w-4" />
                        )}
                        Search
                      </button>
                    </div>
                  </div>

                  {searchedImages.length > 0 && (
                    <div className="space-y-2">
                      <label className="text-muted-foreground text-xs font-medium">
                        Select an image
                      </label>
                      <div className="grid grid-cols-4 gap-2">
                        {searchedImages.map((img) => (
                          <button
                            key={img.id}
                            type="button"
                            onClick={() => {
                              setFeaturedImage(img.url);
                              setSearchedImages([]);
                            }}
                            className={`group relative aspect-video overflow-hidden rounded-lg border-2 transition-all ${
                              featuredImage === img.url
                                ? 'border-primary ring-primary ring-2'
                                : 'hover:border-primary/50 border-transparent'
                            }`}
                          >
                            <img
                              src={img.thumbnailUrl}
                              alt={img.alt}
                              className="h-full w-full object-cover"
                            />
                            {featuredImage === img.url && (
                              <div className="bg-primary absolute inset-0 flex items-center justify-center bg-opacity-20">
                                <Check className="h-5 w-5 text-white drop-shadow-lg" />
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Generate AI image prompts */}
                  <div className="border-t pt-3">
                    <div className="space-y-2">
                      <label className="text-muted-foreground text-xs font-medium">
                        Generate AI image prompts
                      </label>
                      <div className="grid grid-cols-4 gap-2">
                        {[
                          { value: 'photo', label: 'Photo', icon: '📷' },
                          { value: 'illustration', label: 'Illustration', icon: '🎨' },
                          { value: 'digital-art', label: 'Digital', icon: '✨' },
                          { value: '3d', label: '3D', icon: '🎮' },
                        ].map((style) => (
                          <button
                            key={style.value}
                            type="button"
                            onClick={() => setImageStyle(style.value as typeof imageStyle)}
                            className={`rounded-lg border p-1.5 text-center text-xs transition-all ${
                              imageStyle === style.value
                                ? 'border-primary bg-primary/10'
                                : 'hover:bg-muted'
                            }`}
                          >
                            <span className="block text-base">{style.icon}</span>
                            {style.label}
                          </button>
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={async () => {
                          const promptText = imagePrompt.trim() || title.trim();
                          if (!promptText) return;
                          setGeneratingImagePrompts(true);
                          try {
                            const response = await aiHelpers.generateImagePrompts(
                              promptText,
                              imageStyle
                            );
                            if (response.data.success && response.data.data?.prompts) {
                              setSuggestedImagePrompts(response.data.data.prompts);
                              toast.success('Image prompts generated!');
                            }
                          } catch {
                            toast.error('Failed to generate prompts');
                          }
                          setGeneratingImagePrompts(false);
                        }}
                        disabled={generatingImagePrompts || (!imagePrompt.trim() && !title.trim())}
                        className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-violet-500 to-purple-600 px-3 py-2 text-sm font-medium text-white transition-all hover:from-violet-600 hover:to-purple-700 disabled:opacity-50"
                      >
                        {generatingImagePrompts ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Palette className="h-4 w-4" />
                        )}
                        Generate Prompts for AI Image Tools
                      </button>
                    </div>

                    {suggestedImagePrompts.length > 0 && (
                      <div className="mt-3 space-y-2">
                        <p className="text-muted-foreground text-xs font-medium">
                          Click the image button to create one with AI, or copy for other tools:
                        </p>
                        {suggestedImagePrompts.map((prompt, index) => (
                          <div
                            key={index}
                            className="bg-background group flex items-start gap-2 rounded-lg border p-2"
                          >
                            <p className="flex-1 text-xs">{prompt}</p>
                            <div className="flex gap-1">
                              <button
                                type="button"
                                disabled={generatingImage !== null}
                                onClick={async () => {
                                  setGeneratingImage(prompt);
                                  try {
                                    const response = await aiHelpers.createImage(
                                      prompt,
                                      '1792x1024',
                                      'standard'
                                    );
                                    if (response.data.success && response.data.data?.url) {
                                      setFeaturedImage(response.data.data.url);
                                      toast.success('Image generated and set as featured image!');
                                      setSuggestedImagePrompts([]);
                                    } else {
                                      toast.error(
                                        response.data.error?.message || 'Failed to generate image'
                                      );
                                    }
                                  } catch (err: any) {
                                    toast.error(
                                      err?.response?.data?.error?.message ||
                                        err?.message ||
                                        'Failed to generate image'
                                    );
                                  }
                                  setGeneratingImage(null);
                                }}
                                className="hover:bg-primary/10 text-primary rounded p-1 text-xs font-medium transition-colors disabled:opacity-50"
                              >
                                {generatingImage === prompt ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <ImagePlus className="h-3 w-3" />
                                )}
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  navigator.clipboard.writeText(prompt);
                                  toast.success('Prompt copied!');
                                }}
                                className="hover:bg-muted rounded p-1 opacity-0 transition-opacity group-hover:opacity-100"
                              >
                                <Copy className="h-3 w-3" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* SEO */}
          <div className="border-t pt-4">
            <h4 className="mb-3 font-medium">SEO Settings</h4>
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Meta Title</label>
                <input
                  type="text"
                  value={metaTitle}
                  onChange={(e) => setMetaTitle(e.target.value)}
                  placeholder="SEO title (max 70 characters)"
                  maxLength={70}
                  className="bg-background focus:ring-primary w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Meta Description</label>
                <textarea
                  value={metaDescription}
                  onChange={(e) => setMetaDescription(e.target.value)}
                  placeholder="SEO description (max 160 characters)"
                  maxLength={160}
                  rows={2}
                  className="bg-background focus:ring-primary w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2"
                />
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
